import { isSuperAdmin } from "@/helpers/UserHelpers";
import AvailabilityStatusBadge from "@/components/atoms/AvailabilityStatusBadge";
import Avatar from "@/components/atoms/Avatar";
import ErrorComponent from "@/components/atoms/ErrorComponent";
import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import UnknownBadge from "@/components/atoms/UnknownBadge";
import Flex from "@/components/atoms/Flex";
import Loader from "@/components/atoms/Loader";
import SoftBadge from "@/components/atoms/SoftBadge";
import AreYouSurToContinueModal from "@/components/molecules/modals/AreYouSurToContinueModal";
import UserFiltersComponent from "@/components/molecules/table-filters/UserFilters";
import AllUsersHeader from "@/components/molecules/table-headers/AllUsersHeader";
import Table from "@/components/molecules/table/Table";
import {
  checkIfUserHasRoles,
  getColorFromRole,
  getReelRoleName,
  getReelStateColor,
  getReelStateName,
} from "@/helpers/UserHelpers";
import { splitText } from "@/helpers/utils";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTableState } from "@/hooks/useTableState";
import IUser, { UserFilters } from "@/interfaces/IUser";
import { useLoginAsMutation } from "@/services/auth";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { useDestroyUserMutation, useGetUsersQuery } from "@/services/users";
import {
  faEdit,
  faEye,
  faTrash,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { ColumnDef, TableState } from "@tanstack/react-table";
import classNames from "classnames";
import { DateTime } from "luxon";
import { ElementRef, FC, Fragment, useRef } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useCookies } from "react-cookie";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContentProps } from "react-toastify";

const UsersView: FC = () => {
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const user = useSelector(selectCurrentUser);
  const [deleteUserAsync] = useDestroyUserMutation();
  const [loginAsAsync] = useLoginAsMutation();
  const [_, setCookie] = useCookies(["te_co"]);
  const navigation = useNavigate();

  const [state, setState] = useTableState<Partial<TableState> & UserFilters>(
    "users",
    {
      pagination: {
        pageIndex: 0,
        pageSize: 15,
      },
      globalFilter: "",
      isActive: true,
    },
  );

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);

  const { data, isLoading, isError, error } = useGetUsersQuery({
    ...state.pagination,
    query: debouncedQuery,
    roles: state.roles,
    isActive: state.isActive,
    excludeImported: true,
    ...(state.sorting?.[0] && {
      sortBy: state.sorting[0].id,
      sortDirection: state.sorting[0].desc ? "desc" : "asc",
    }),
  });

  const onDelete = async (id: string) => {
    toast.promise(deleteUserAsync(id).unwrap(), {
      pending: "Suppression du statut en cours ...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return (
            <p className="mb-0 text-center">
              L'utilisateur à bien été supprimé 🤩
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          return "Une erreur est survenue";
        },
      },
    });
  };

  const onLoginAs = async (id: string) => {
    toast.promise(loginAsAsync(id).unwrap(), {
      pending: "Connexion a l'utilisateur en cours ...",
      success: {
        render({ data }) {
          setCookie("te_co", data?.token, { path: "/" });
          navigation("/");
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              C&apos;est un plaisir de vous revoir{" "}
              <b>{data?.user.firstname} 🤩</b>
            </p>
          );
        },
      },
      error: "Une erreur est survenue",
    });
  };

  const columns = useRef<ColumnDef<IUser>[]>([
    {
      header: "Actions",
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: ({ id, roles }) => (
        <Fragment>
          <Link to={`/users/${id}`} className="btn btn-primary btn-sm">
            <FontAwesomeIcon icon={faEye} />
          </Link>
          {(!isSuperAdmin({ roles }) || isSuperAdmin(user)) && (
            <Link to={`/users/${id}/edit`} className="btn btn-info btn-sm ms-2">
              <FontAwesomeIcon icon={faEdit} />
            </Link>
          )}
          {user?.id !== id &&
            (!isSuperAdmin({ roles }) || isSuperAdmin(user)) && (
              <Fragment>
                <Button
                  variant="danger"
                  size="sm"
                  className="ms-2"
                  onClick={() => DeleteModalRef.current?.showOrHiddenModal(id)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
                {checkIfUserHasRoles(user, ["ADMIN"]) && (
                  <OverlayTrigger
                    overlay={<Tooltip id="nextTooltip">Se connecter</Tooltip>}
                  >
                    <Button
                      variant="success"
                      size="sm"
                      className="ms-2"
                      onClick={() => onLoginAs(id)}
                    >
                      <FontAwesomeIcon icon={faUser} />
                    </Button>
                  </OverlayTrigger>
                )}
              </Fragment>
            )}
        </Fragment>
      ),
      id: "actions",
      cell: (info) => info.getValue(),
    },
    {
      header: "#",
      footer: (props) => props.column.id,
      accessorFn: ({ id }) => splitText(id, 10),
      id: "id",
      cell: (info) => info.getValue(),
    },
    {
      header: "État",
      footer: (props) => props.column.id,
      accessorFn: ({ isActive }) => (
        <SoftBadge
          bg={getReelStateColor(isActive)}
          className="my-2 text-xl-center"
        >
          {getReelStateName(isActive)}
        </SoftBadge>
      ),
      id: "isActive",
      cell: (info) => info.getValue(),
    },
    // Suivi du déploiement des disponibilités, utile aux seuls ADMIN et CSM.
    ...(checkIfUserHasRoles(user, ["ADMIN", "CSM"])
      ? [
          {
            header: "Disponibilités",
            footer: (props) => props.column.id,
            accessorFn: ({ availabilitySetAt }) => (
              <AvailabilityStatusBadge setAt={availabilitySetAt} />
            ),
            id: "availabilitySetAt",
            cell: (info) => info.getValue(),
          } as ColumnDef<IUser>,
        ]
      : []),
    {
      header: "Roles",
      footer: (props) => props.column.id,
      accessorFn: ({ roles }) => (
        <div>
          {roles.map((role, index) => (
            <SoftBadge
              bg={getColorFromRole([role])}
              className={classNames("my-2 text-xl-center", {
                "ms-1": index !== 0,
              })}
            >
              {getReelRoleName([role])}
            </SoftBadge>
          ))}
        </div>
      ),
      id: "roles",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nom",
      footer: (props) => props.column.id,
      enableSorting: false,
      accessorFn: ({ fullname, id, firstname, avatar }) => (
        <Flex alignItems="center">
          {avatar && (
            <Avatar
              src={avatar.link}
              alt={`${firstname} Avatar`}
              className="me-2"
              size="2xl"
            />
          )}
          <span>
            {fullname} {user?.id === id && <b>(Moi)</b>}
          </span>
        </Flex>
      ),
      id: "fullname",
      cell: (info) => info.getValue(),
    },
    {
      header: "Email",
      footer: (props) => props.column.id,
      accessorFn: ({ email }) => email,
      id: "email",
      cell: (info) => info.getValue(),
    },
    {
      header: "Téléphone",
      footer: (props) => props.column.id,
      accessorFn: ({ phone }) =>
        phone ? <PhoneCallLink number={phone} /> : <UnknownBadge />,
      id: "phone",
      cell: (info) => info.getValue(),
    },
    {
      header: "Crée le",
      footer: (props) => props.column.id,
      accessorFn: (row) =>
        DateTime.fromISO(row.createdAt).toFormat("dd-MM-yyyy"),
      id: "createdAt",
      cell: (info) => info.getValue(),
    },
  ]).current;

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <div className="formalys-page formalys-list-page">
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment supprimé le statut de la entreprise qui a pour id : {{id}} ?"
      />
      <Table
        resource="users"
        HeaderComponent={AllUsersHeader}
        FilterComponent={UserFiltersComponent}
        smallFilters
        columns={columns}
        state={state}
        tableName="users"
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
      />
    </div>
  );
};

export default UsersView;
